import { useState } from 'react';
import {
  Box,
  Button,
  CodeBlock,
  FormattedDateTime,
  Icon,
  List,
  ListItem,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { ObjectAttributeTypeLabel } from '../attributes-editor/object-attribute-type-label';
import { CREATED_IN_CIO_AT, parseJSONAttribute } from '@/utils/attributes';
import { isValidTimestamp } from '@/features/people';

type ObjectAttributeListProps = {
  /** Ordered `{ key: value }` pairs, e.g. from `filteredAttrsWithIdentifiers`. */
  attributes: ReadonlyArray<Record<string, string>>;
  isMultiIdentifier: boolean;
  /** Skip the Identifier badge (the identifiers section labels them already). */
  ignoreIdentifiers?: boolean;
};

type ObjectAttributeListItemProps = {
  attributeKey: string;
  value: string;
  isMultiIdentifier: boolean;
  ignoreIdentifiers?: boolean;
};

function ObjectAttributeListItem({ attributeKey, value, ...props }: ObjectAttributeListItemProps) {
  const [isJsonExpanded, setIsJsonExpanded] = useState(false);
  const jsonValue = parseJSONAttribute(value);
  const isJson = jsonValue !== undefined;
  const isTimestamp = !isJson && isValidTimestamp(value);

  return (
    <ListItem display="flex" alignItems="center" px="300" py="100">
      <Box flex={1}>
        <Box>
          <Text as="span" display="flex" alignItems="center" weight="regular" color="subtle" overflowWrap="words">
            <Text as="span" pr="025" data-test-object-attribute-key>
              {attributeKey}
            </Text>
            {attributeKey === CREATED_IN_CIO_AT ? (
              <Tooltip placement="top" isCustom>
                {(context) => (
                  <>
                    <Icon
                      name="help"
                      size="sm"
                      color="subtle"
                      ref={context.reference.setReference}
                      {...context.reference.getReferenceProps()}
                    />
                    <TooltipContent>Auto-generated by Customer.io.</TooltipContent>
                  </>
                )}
              </Tooltip>
            ) : null}
            <ObjectAttributeTypeLabel
              attribute={attributeKey}
              isMultiIdentifier={props.isMultiIdentifier}
              ignoreIdentifiers={props.ignoreIdentifiers}
            />
          </Text>
        </Box>
        <Box pl="0">
          {isJson ? (
            <Box as="span" display="block">
              <Button variant="secondary" size="sm" mb="100" onClick={() => setIsJsonExpanded(!isJsonExpanded)}>
                {isJsonExpanded ? 'Collapse JSON' : 'Expand JSON'}
              </Button>
            </Box>
          ) : null}

          <Box as="span" display="block" overflowWrap="words" className="fs-mask" data-test-object-attribute-value>
            {jsonValue !== undefined && isJsonExpanded ? (
              <CodeBlock language="json" code={JSON.stringify(jsonValue, null, 2)} withCopyButton={false} />
            ) : value !== '' ? (
              value
            ) : (
              '–'
            )}
          </Box>

          {isTimestamp ? (
            <Box as="span" display="block">
              <FormattedDateTime date={Number(value)} format="medium_with_time" size="xxs" color="subtle" />
            </Box>
          ) : null}
        </Box>
      </Box>
    </ListItem>
  );
}

export function ObjectAttributeList({ attributes, isMultiIdentifier, ignoreIdentifiers }: ObjectAttributeListProps) {
  return (
    <List styleType="none" gap="100">
      {attributes.map((attribute) => {
        const [key = ''] = Object.keys(attribute);
        return (
          <ObjectAttributeListItem
            key={key}
            attributeKey={key}
            value={attribute[key] ?? ''}
            isMultiIdentifier={isMultiIdentifier}
            ignoreIdentifiers={ignoreIdentifiers}
          />
        );
      })}
    </List>
  );
}
